/**
 * Typfout-vangnet voor e-maildomeinen (sessie 23, 10-09-2026).
 *
 * Spiegel van app/core/email_typfouten.py — de lijst zelf komt uit de
 * publieke shop-extras-config (één bron, uitbreiden = deploy), de
 * logica staat hier regel voor regel gelijk aan de Python-versie en aan
 * static/checkout/checkout.js:
 *
 * 1. een bekend domein krijgt nooit een suggestie;
 * 2. een expliciete verschrijving wint;
 * 3. een bekende TLD-verschrijving (.con → .com) wint als het resultaat
 *    een bekend domein is;
 * 4. anders het bekende domein op Damerau-Levenshtein-afstand 1 (2 vanaf
 *    tien tekens) met dezelfde beginletter en een naam van minstens vier
 *    tekens vóór de punt (mc.com wordt nooit me.com).
 *
 * Conservatief: liever geen suggestie dan een verkeerde. Er wordt nooit
 * automatisch vervangen; de klant klikt zelf.
 */

export interface EmailTypfoutConfig {
  bekend: string[];
  typfouten: Record<string, string>;
  tld_typfouten: Record<string, string>;
  min_lengte?: number;
  min_basis?: number;
  lang_domein?: number;
}

function damerauLevenshtein(a: string, b: string, maximum: number): number {
  if (Math.abs(a.length - b.length) > maximum) return maximum + 1;
  let vorige2: number[] | null = null;
  let vorige: number[] = [];
  for (let j = 0; j <= b.length; j++) vorige.push(j);
  for (let i = 1; i <= a.length; i++) {
    const huidige: number[] = [i];
    let laagste = i;
    for (let j = 1; j <= b.length; j++) {
      const kosten = a.charAt(i - 1) === b.charAt(j - 1) ? 0 : 1;
      let w = Math.min(vorige[j] + 1, huidige[j - 1] + 1, vorige[j - 1] + kosten);
      if (
        vorige2 !== null &&
        i > 1 &&
        j > 1 &&
        a.charAt(i - 1) === b.charAt(j - 2) &&
        a.charAt(i - 2) === b.charAt(j - 1)
      ) {
        w = Math.min(w, vorige2[j - 2] + 1);
      }
      huidige.push(w);
      if (w < laagste) laagste = w;
    }
    if (laagste > maximum) return maximum + 1;
    vorige2 = vorige;
    vorige = huidige;
  }
  return vorige[b.length];
}

export function suggereerDomein(
  domein: string | null | undefined,
  config: EmailTypfoutConfig | null | undefined,
): string | null {
  if (!config || !config.bekend || config.bekend.length === 0) return null;
  const d = (domein ?? "").trim().toLowerCase();
  if (!d || !d.includes(".") || d.includes(" ")) return null;
  const bekend = config.bekend;
  if (bekend.includes(d)) return null;
  const expliciet = (config.typfouten ?? {})[d];
  if (expliciet) return expliciet;
  const punt = d.lastIndexOf(".");
  const basis = d.slice(0, punt);
  const tld = d.slice(punt + 1);
  const goedeTld = (config.tld_typfouten ?? {})[tld];
  if (basis && goedeTld && bekend.includes(`${basis}.${goedeTld}`)) {
    return `${basis}.${goedeTld}`;
  }
  if (d.length < (config.min_lengte ?? 5)) return null;
  if (basis.length < (config.min_basis ?? 4)) return null;
  const maximum = d.length >= (config.lang_domein ?? 10) ? 2 : 1;
  let beste: string | null = null;
  let besteAfstand = maximum + 1;
  for (const kandidaat of bekend) {
    if (kandidaat.charAt(0) !== d.charAt(0)) continue;
    const afstand = damerauLevenshtein(d, kandidaat, maximum);
    if (afstand < besteAfstand) {
      beste = kandidaat;
      besteAfstand = afstand;
    }
  }
  return besteAfstand <= maximum ? beste : null;
}

/** Het gecorrigeerde adres, of null als er niets te suggereren is. */
export function suggereerEmail(
  adres: string | null | undefined,
  config: EmailTypfoutConfig | null | undefined,
): string | null {
  const schoon = (adres ?? "").trim();
  if (schoon.split("@").length !== 2) return null;
  const apen = schoon.indexOf("@");
  const lokaal = schoon.slice(0, apen);
  if (!lokaal) return null;
  const beter = suggereerDomein(schoon.slice(apen + 1), config);
  return beter ? `${lokaal}@${beter}` : null;
}

/** Het domein van een adres (voor de PII-vrije meting). */
export function domeinVan(adres: string): string {
  return adres.slice(adres.indexOf("@") + 1).toLowerCase();
}
